import React, {
  createContext,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Vector2 } from 'three'

type MarkerContextType = {
  activeTooltipId: string | null
  tooltipScreenPosition: Vector2 | null
  startTooltipHideTimer: () => void
  handleMarkerActivate: (observationId: string, screenPosition: Vector2) => void
  handleTooltipEnter: () => void
  tooltipTimeoutRef: MutableRefObject<NodeJS.Timeout | undefined>
}

export const MarkerContext = createContext<MarkerContextType | undefined>(
  undefined
)
export const MarkerContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null)
  const tooltipTimeoutRef = useRef<NodeJS.Timeout>()

  const [tooltipScreenPosition, setTooltipScreenPosition] =
    useState<Vector2 | null>(null)

  const startTooltipHideTimer = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }
    tooltipTimeoutRef.current = setTimeout(() => {
      setActiveTooltipId(null)
      setTooltipScreenPosition(null)
      tooltipTimeoutRef.current = undefined
    }, 1200)
  }, [])

  const handleMarkerActivate = useCallback(
    (observationId: string, screenPosition: Vector2) => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
        tooltipTimeoutRef.current = undefined
      }
      setActiveTooltipId(observationId)
      setTooltipScreenPosition(screenPosition)
    },
    []
  )

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }
  }, [])

  const contextValue = React.useMemo(() => {
    const handleTooltipEnter = () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
        tooltipTimeoutRef.current = undefined
      }
    }

    return {
      activeTooltipId,
      tooltipScreenPosition,
      tooltipTimeoutRef,
      startTooltipHideTimer,
      handleMarkerActivate,
      handleTooltipEnter,
    }
  }, [
    activeTooltipId,
    tooltipScreenPosition,
    tooltipTimeoutRef,
    startTooltipHideTimer,
    handleMarkerActivate,
  ])

  return (
    <MarkerContext.Provider value={contextValue}>
      {children}
    </MarkerContext.Provider>
  )
}

export const useMarkerContext = (): MarkerContextType => {
  const context = useContext(MarkerContext)
  if (context === undefined) {
    throw new Error(
      'useMarkerContext must be used within a MarkerContextProvider'
    )
  }
  return context
}
